/**
 * Wiederverwendbare Gesamtsuche für Beteiligte (Kundenfeedback 18.10.2026,
 * XII): Name eingeben → eigene Kontakte, in KARS registrierte Organisationen
 * und das Versicherungsverzeichnis werden in einer Anfrage durchsucht.
 *
 *   const search = useContactSearch()
 *   search.setQuery('KS Gut')   // entprellt, ab 2 Zeichen
 *   search.hits.value           // ContactSearchHit[] (utils/contactSearchHits)
 *
 * Mit eigener `fetch(q)` (z. B. Termin-Partnersuche, XVI) teilen andere Suchen
 * nur Entprellung, Mindestlänge und Race-Schutz; ihr Ergebnis liegt dann
 * unverändert in `response` (`hits` bleibt leer).
 *
 * Race-Schutz: Antworten zu veralteten Suchbegriffen werden verworfen.
 */
import type { Contact } from '@/services/contactModel'
import { computed, onScopeDispose, ref, shallowRef } from 'vue'
import { CONTACT_SEARCH_MIN_LENGTH, type ContactSearchResponse, searchContacts } from '@/services/contactSearchApi'
import { buildContactSearchHits, type ContactSearchHit } from '@/utils/contactSearchHits'

const DEFAULT_DEBOUNCE_MS = 250

export interface UseContactSearchOptions<R = ContactSearchResponse> {
  debounceMs?: number
  /** Bereits bekannte eigene Kontakte – registrierte Dubletten werden ausgeblendet. */
  knownContacts?: () => readonly Contact[]
  /** Suchanfrage (getrimmter Begriff); Standard: GET /protected/contacts/search. */
  fetch?: (q: string) => Promise<R>
  /** Mindestlänge; Standard: getrimmt ab CONTACT_SEARCH_MIN_LENGTH Zeichen. */
  isReady?: (q: string) => boolean
}

function defaultReady (q: string): boolean {
  return q.trim().length >= CONTACT_SEARCH_MIN_LENGTH
}

export function useContactSearch<R = ContactSearchResponse> (options: UseContactSearchOptions<R> = {}) {
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS
  const isReady = options.isReady ?? defaultReady
  const fetchResults = options.fetch ?? (searchContacts as unknown as (q: string) => Promise<R>)
  const query = ref('')
  const response = shallowRef<R | null>(null)
  const loading = ref(false)
  const error = ref('')
  /** true, sobald zum aktuellen Suchbegriff eine Antwort vorliegt. */
  const searched = ref(false)
  let timer: ReturnType<typeof setTimeout> | null = null
  let latest = ''

  const isQueryLongEnough = computed(() => isReady(query.value))
  const hits = computed<ContactSearchHit[]>(() => (options.fetch
    ? []
    : buildContactSearchHits(response.value as ContactSearchResponse | null, options.knownContacts ? options.knownContacts() : [])))

  function cancelTimer () {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  async function run (text: string) {
    latest = text
    loading.value = true
    error.value = ''
    try {
      const result = await fetchResults(text)
      if (latest !== text) return
      response.value = result
      searched.value = true
    } catch (error_: any) {
      if (latest !== text) return
      response.value = null
      searched.value = true
      error.value = error_?.message || 'Suche fehlgeschlagen'
    } finally {
      if (latest === text) loading.value = false
    }
  }

  function setQuery (text: string) {
    query.value = text
    cancelTimer()
    const trimmed = text.trim()
    if (!isReady(trimmed)) {
      latest = ''
      response.value = null
      searched.value = false
      loading.value = false
      error.value = ''
      return
    }
    loading.value = true
    searched.value = false
    timer = setTimeout(() => run(trimmed), debounceMs)
  }

  /** Erneut suchen (z. B. nach Übernahme in die Kontakte). */
  function refresh () {
    const trimmed = query.value.trim()
    if (isReady(trimmed)) return run(trimmed)
    return Promise.resolve()
  }

  /** Geplante bzw. laufende Suche verwerfen; bisherige Treffer bleiben stehen. */
  function cancel () {
    cancelTimer()
    latest = ''
    loading.value = false
  }

  function clear () {
    setQuery('')
  }

  // Komponente entfernt (bzw. effectScope beendet): geplante Suche verwerfen.
  onScopeDispose(cancelTimer)

  return { query, response, hits, loading, error, searched, isQueryLongEnough, setQuery, refresh, clear, cancel }
}
